/**
 * Typed environment variable access — DSGVO + healthcare-grade.
 *
 * Why a separate module?
 * 1. **Single source of truth** — every `import.meta.env.VITE_...` lives here
 *    so we never typo a variable name across the codebase.
 * 2. **Fail fast** — `assertEnv()` is called once at app boot in `main.tsx`.
 *    If a required variable is missing we throw with a *helpful* message
 *    instead of crashing five seconds later inside a Supabase call.
 * 3. **Type safety** — every consumer gets a `string` (never `string | undefined`)
 *    for required variables, so downstream code stays clean.
 *
 * Adding a new variable:
 *   1. Add a getter below.
 *   2. If it is required, list its key in `REQUIRED_KEYS`.
 *   3. Add it to `.env.example` with a placeholder value.
 *   That is the *only* place new variables need to be touched.
 */

type EnvKey =
  | 'VITE_SUPABASE_URL'
  | 'VITE_SUPABASE_PUBLISHABLE_KEY'
  | 'VITE_SUPABASE_PROJECT_ID'
  | 'VITE_PUBLIC_APP_URL'
  | 'VITE_SENTRY_DSN'
  | 'VITE_STRIPE_PUBLISHABLE_KEY'
  | 'VITE_NK_PRICE_PRO_MONTHLY'
  | 'VITE_NK_PRICE_PRO_YEARLY'
  | 'VITE_NK_PRICE_TEAM';

const REQUIRED_KEYS: readonly EnvKey[] = [
  'VITE_SUPABASE_URL',
  'VITE_SUPABASE_PUBLISHABLE_KEY',
] as const;

function read(key: EnvKey): string | undefined {
  // Vite injects env at build time; cast through Record to avoid `any`.
  const raw = (import.meta.env as Record<string, string | undefined>)[key];
  if (raw == null) return undefined;
  const trimmed = String(raw).trim();
  return trimmed === '' ? undefined : trimmed;
}

function required(key: EnvKey): string {
  const value = read(key);
  if (value == null) {
    throw new Error(
      `[env] Required environment variable ${key} is missing. ` +
        `Set it in .env (see .env.example) before starting the app.`,
    );
  }
  return value;
}

/**
 * Validate that every required variable is present. Call this once from
 * `main.tsx` before any other module touches Supabase / Stripe / etc.
 *
 * Throws an `Error` listing *every* missing variable so the user can fix
 * them all in one go instead of one-by-one.
 */
export function assertEnv(): void {
  const missing = REQUIRED_KEYS.filter((key) => read(key) == null);
  if (missing.length > 0) {
    throw new Error(
      `[env] Missing required environment variables:\n` +
        missing.map((k) => `  - ${k}`).join('\n') +
        `\n\nCopy .env.example to .env and fill in the values.`,
    );
  }
}

/**
 * Strongly-typed environment accessors.
 *
 * Required values are returned as plain `string`. Optional ones return
 * `string | undefined` so the caller is forced to handle the absence.
 */
export const env = {
  // ── Supabase ──────────────────────────────────────────────────────────
  get supabaseUrl(): string {
    return required('VITE_SUPABASE_URL');
  },
  get supabaseAnonKey(): string {
    return required('VITE_SUPABASE_PUBLISHABLE_KEY');
  },
  get supabaseProjectId(): string | undefined {
    return read('VITE_SUPABASE_PROJECT_ID');
  },
  get publicAppUrl(): string {
    return read('VITE_PUBLIC_APP_URL') ?? 'https://nutriflow-pro-93.vercel.app';
  },

  // ── Sentry ────────────────────────────────────────────────────────────
  get sentryDsn(): string | undefined {
    return read('VITE_SENTRY_DSN');
  },

  // ── Stripe ────────────────────────────────────────────────────────────
  get stripePublishableKey(): string | undefined {
    return read('VITE_STRIPE_PUBLISHABLE_KEY');
  },

  // ── NutriKnows pricing ────────────────────────────────────────────────
  get nkPriceProMonthly(): string | undefined {
    return read('VITE_NK_PRICE_PRO_MONTHLY');
  },
  get nkPriceProYearly(): string | undefined {
    return read('VITE_NK_PRICE_PRO_YEARLY');
  },
  get nkPriceTeam(): string | undefined {
    return read('VITE_NK_PRICE_TEAM');
  },

  // ── Build flags (always present) ──────────────────────────────────────
  get mode(): string {
    return import.meta.env.MODE;
  },
  get isProd(): boolean {
    return import.meta.env.PROD;
  },
  get isDev(): boolean {
    return import.meta.env.DEV;
  },
} as const;

export type Env = typeof env;
